export interface TrainChoice {
  id: string;
  label: string;
  correct: boolean;
  mediaUrl?: string;
}

export interface TrainQuestion {
  id: string;
  prompt: string;
  wagonStyle?: 'wide' | 'narrow';
  choices: TrainChoice[];
  promptMediaUrl?: string;
}

export interface TrainLevel {
  id: number;
  questions: TrainQuestion[];
}

export type LevelPhase = 'playing' | 'level-complete' | 'won' | 'lost';

export interface LevelProgress {
  levelIndex: number;
  questionIndex: number;
  phase: LevelPhase;
}

export type WagonStatus = 'completed' | 'active' | 'pending';

export interface WagonRenderState {
  question: TrainQuestion;
  questionIndex: number;
  status: WagonStatus;
}

function createDemoQuestion(
  id: string,
  prompt: string,
  labels: string[],
  correctAnswer: string,
): TrainQuestion {
  return {
    id,
    prompt,
    choices: labels.map((label, index) => ({
      id: `${id}-choice-${index + 1}`,
      label,
      correct: label === correctAnswer,
    })),
  };
}

export const DEMO_LEVELS: TrainLevel[] = [
  {
    id: 1,
    questions: [
      createDemoQuestion('l1-q1', '2 × 4', ['6', '8', '9', '12'], '8'),
    ],
  },
  {
    id: 2,
    questions: [
      createDemoQuestion('l2-q1', '3 × 3', ['6', '7', '9', '12'], '9'),
      createDemoQuestion('l2-q2', '12 ÷ 3', ['3', '4', '5', '6'], '4'),
    ],
  },
  {
    id: 3,
    questions: [
      createDemoQuestion('l3-q1', '7 + 5', ['10', '11', '12', '13'], '12'),
      createDemoQuestion('l3-q2', '15 − 6', ['7', '8', '9', '10'], '9'),
      createDemoQuestion('l3-q3', '4 × 5', ['16', '18', '20', '24'], '20'),
    ],
  },
  {
    id: 4,
    questions: [
      createDemoQuestion('l4-q1', '24 ÷ 6', ['3', '4', '5', '6'], '4'),
      createDemoQuestion('l4-q2', '9 + 8', ['15', '16', '17', '18'], '17'),
      createDemoQuestion('l4-q3', '18 − 7', ['9', '10', '11', '12'], '11'),
      createDemoQuestion('l4-q4', '6 × 4', ['18', '20', '24', '28'], '24'),
    ],
  },
  {
    id: 5,
    questions: [
      createDemoQuestion('l5-q1', '35 ÷ 5', ['5', '6', '7', '8'], '7'),
      createDemoQuestion('l5-q2', '16 + 9', ['23', '24', '25', '26'], '25'),
      createDemoQuestion('l5-q3', '30 − 12', ['16', '17', '18', '19'], '18'),
      createDemoQuestion('l5-q4', '7 × 6', ['36', '40', '42', '48'], '42'),
      createDemoQuestion('l5-q5', '48 ÷ 8', ['5', '6', '7', '8'], '6'),
    ],
  },
];

export function createInitialLevelProgress(): LevelProgress {
  return { levelIndex: 0, questionIndex: 0, phase: 'playing' };
}

export function getActiveQuestion(progress: LevelProgress, levels: TrainLevel[] = DEMO_LEVELS): TrainQuestion {
  return levels[progress.levelIndex].questions[progress.questionIndex];
}

export function getWagonRenderStates(progress: LevelProgress, levels: TrainLevel[] = DEMO_LEVELS): WagonRenderState[] {
  return levels[progress.levelIndex].questions.map((question, questionIndex) => ({
    question,
    questionIndex,
    status: questionIndex < progress.questionIndex
      ? 'completed'
      : questionIndex === progress.questionIndex
        ? 'active'
        : 'pending',
  }));
}

export function completeActiveQuestion(progress: LevelProgress, levels: TrainLevel[] = DEMO_LEVELS): LevelProgress {
  if (progress.phase !== 'playing') return progress;

  const level = levels[progress.levelIndex];
  const isLastQuestion = progress.questionIndex === level.questions.length - 1;
  if (!isLastQuestion) return { ...progress, questionIndex: progress.questionIndex + 1 };

  const isLastLevel = progress.levelIndex === levels.length - 1;
  return { ...progress, phase: isLastLevel ? 'won' : 'level-complete' };
}

export function beginNextLevel(progress: LevelProgress): LevelProgress {
  if (progress.phase !== 'level-complete') return progress;
  return { levelIndex: progress.levelIndex + 1, questionIndex: 0, phase: 'playing' };
}
